import React from 'react';
import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import {
  generateFilter,
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  ResourceAccessLevels,
  ResourceAccessLevelsResponse,
} from 'types/Share/ResourceAccessLevels';
import { DropdownOption } from 'types/common/DropdownOption';
import { AccessLevel } from 'types/Share/AccessLevels';

const getResourceAccessLevels = async (
  resourceTypeID: string | null | undefined
) => {
  if (!resourceTypeID) {
    return Promise.reject('resourceTypeID can not be null');
  }
  const queryArray = [
    generateFilter('resourceTypeID', resourceTypeID),
    generateIncludeQuery(['accessLevel']),
  ];
  const url = generateURL(
    SPARK_RESOURCE_TYPES.RESOURCE_ACCESS_LEVELS,
    queryArray
  );

  return fetchWrapper<ResourceAccessLevelsResponse>({
    url: url,
    method: 'GET',
  });
};

export function useResourceAccessLevels(
  resourceTypeID: string | null | undefined
) {
  const queryResult = useQuery(
    [SPARK_RESOURCE_TYPES.RESOURCE_ACCESS_LEVELS, resourceTypeID],
    () => getResourceAccessLevels(resourceTypeID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `Failed to get Resource access levels: ${error.message}`
          );
        }
      },
      enabled: Boolean(resourceTypeID),
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const { data } = queryResult;
  const { data: resourceAccessLevels, included: accessLevels } = data || {};

  const idMap = React.useMemo(() => {
    return resourceAccessLevels?.reduce<Record<string, ResourceAccessLevels>>(
      (acc, curr) => {
        return { ...acc, [curr.id]: curr };
      },
      {}
    );
  }, [resourceAccessLevels]);

  const accessLevelDropdownOptions: DropdownOption[] =
    React.useMemo(
      () =>
        accessLevels?.map((accessLevel) => ({
          label: accessLevel.attributes.name,
          value: accessLevel.id,
          iconUrl: accessLevel.attributes.iconURL,
          description: '',
        })),
      [accessLevels]
    ) || [];

  const accessLevelsIdMap = React.useMemo(() => {
    return accessLevels?.reduce<Record<string, AccessLevel>>((acc, curr) => {
      return { ...acc, [curr.id]: curr };
    }, {});
  }, [accessLevels]);

  const getResourceAccessLevelID = (
    resourceTypeID: string | undefined,
    accessLevelID: string | undefined
  ) => {
    return resourceAccessLevels?.find(
      (resourceAccessLevel) =>
        resourceAccessLevel.attributes.accessLevelID === accessLevelID &&
        resourceAccessLevel.attributes.resourceTypeID === resourceTypeID
    )?.id;
  };

  const getAccessLevelIDByResourceAccessLevelID = (
    resourceAccessLevelID: string | undefined
  ) => {
    if (!idMap || !resourceAccessLevelID) {
      return;
    }
    return idMap[resourceAccessLevelID].attributes.accessLevelID;
  };

  return {
    resourceShareIdMap: idMap,
    accessLevels,
    accessLevelDropdownOptions,
    accessLevelsIdMap,
    getResourceAccessLevelID,
    getAccessLevelIDByResourceAccessLevelID,
    ...queryResult,
  };
}
